import React, { useEffect, Fragment, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import Swal from "sweetalert2";
import { UncontrolledTooltip } from "reactstrap";

import Breadcrumb from "../../../../components/common/breadcrumb";
import { showToast } from "../../../../components/ToastifyNotification";

import { DeletePatientHistory, GetPatientHistories } from "../../../../api/ipd/patientHistoryAPI";
import { DeleteMlc, GetMlcs } from "../../../../api/ipd/mlcAPI";

const ListMlcEntry = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [search, setSearch] = useState("");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  // ================= FETCH DATA =================
  const fetchData = async () => {
    dispatch({ type: "LOADER_START" });
    try {
      const response = await GetMlcs();
      if (response.success) {
        showToast("success", response.message);
        const formattedData = response.data.map((item, index) => ({
          index: index + 1,
          id: item._id,

          mlcNo: item?.mlcNo || "",
          ipdNo: item?.ipdEntryId?.ipdNo || "",
          patientName: item?.ipdEntryId?.patientId?.patientName || "",
          sex: item?.ipdEntryId?.patientId?.sex || "",
          age: item?.ipdEntryId?.patientId?.age || "",

          natureOfInjury: item.natureOfInjury || "",
          diagnosis: item.diagnosis || "",
          historyAndPhysicalExamination: item.historyAndPhysicalExamination || "",
          treatment: item.treatment || "",
          markOfIdentification: item.markOfIdentification || "",
          remark: item.remark || "",
          thanaName: item.thanaName || "",
          patientBoughtBy: item.patientBoughtBy || "",
          placeOfAccident: item.placeOfAccident || "",
        }));

        setData(formattedData);
        setFilteredData(formattedData);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message);
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // ================= SEARCH =================
  useEffect(() => {
    const results = data.filter(item =>
      Object.values(item)
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
    setFilteredData(results);
  }, [search, data]);

  // ================= ACTIONS =================
  const handleEditClick = id => {
    navigate(`/ipd/transaction/mlc-entry/edit/${id}`);
  };

  const handleDeleteClick = id => {
    Swal.fire({
      title: "Are you sure?",
      text: "This delivery entry will be deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    }).then(async result => {
      if (result.isConfirmed) {
        dispatch({ type: "LOADER_START" });
        try {
          const response = await DeleteMlc(id);
          if (response.success) {
            showToast("success", response.message);
            const newData = data.filter(item => item.id !== id);
            setData(newData);
            setFilteredData(newData);
          } else {
            showToast("error", response.message);
          }
        } catch (error) {
          showToast("error", error.message);
        } finally {
          dispatch({ type: "LOADER_STOP" });
        }
      }
    });
  };

  // ================= TABLE COLUMNS =================
  const columns = [
    { name: "S.No", selector: row => row.index, width: "6%" },
    
    { name: "MLC No", selector: row => row.mlcNo, sortable: true, width: "10%" },
    { name: "IPD No", selector: row => row.ipdNo, sortable: true, width: "10%" },
    { name: "Patient Name", selector: row => row.patientName, sortable: true, width: "15%" },
    { name: "Sex", selector: row => row.sex, width: "6%" },
    { name: "Age", selector: row => row.age, width: "6%" },

    { name: "Nature of Injury", selector: row => row.natureOfInjury, sortable: true, width: "15%" },
    { name: "Diagnosis", selector: row => row.diagnosis, sortable: true, width: "15%" },
    { name: "History and Physical Examination", selector: row => row.historyAndPhysicalExamination, sortable: true, width: "15%" },
    { name: "Treatment", selector: row => row.treatment, sortable: true, width: "15%" },
    { name: "Mark of Identification", selector: row => row.markOfIdentification, sortable: true, width: "15%" },
    { name: "Remark", selector: row => row.remark, sortable: true, width: "15%" },
    { name: "Thana Name", selector: row => row.thanaName, sortable: true, width: "15%" },
    { name: "Patient Bought By", selector: row => row.patientBoughtBy, sortable: true, width: "15%" },
    { name: "Place of Accident", selector: row => row.placeOfAccident, sortable: true, width: "15%" },

    {
      name: "Actions",
      cell: row => (
        <div>
          <button
            className="btn btn-primary me-2"
            onClick={() => handleEditClick(row.id)}
            id={`edit-${row.id}`}
          >
            <i className="fa fa-pencil" />
          </button>
          <UncontrolledTooltip target={`edit-${row.id}`}>
            Edit
          </UncontrolledTooltip>

          <button
            className="btn btn-danger"
            onClick={() => handleDeleteClick(row.id)}
            id={`delete-${row.id}`}
          >
            <i className="fa fa-trash" />
          </button>
          <UncontrolledTooltip target={`delete-${row.id}`}>
            Delete
          </UncontrolledTooltip>
        </div>
      ),
      ignoreRowClick: true,
      button: true,
      width: "20%"
    }
  ];

  // ================= CSV EXPORT =================
  const headers = [
    { label: "S.No", key: "index" },
    { label: "MLC No", key: "mlcNo" },
    { label: "IPD No", key: "ipdNo" },
    { label: "Patient Name", key: "patientName" },
    { label: "Sex", key: "sex" },
    { label: "Age", key: "age" },
    { label: "Nature of Injury", key: "natureOfInjury" },
    { label: "Diagnosis", key: "diagnosis" },
    { label: "History and Physical Examination", key: "historyAndPhysicalExamination" },
    { label: "Treatment", key: "treatment" },
    { label: "Mark of Identification", key: "markOfIdentification" },
    { label: "Remark", key: "remark" },
    { label: "Thana Name", key: "thanaName" },
    { label: "Patient Bought By", key: "patientBoughtBy" },
    { label: "Place of Accident", key: "placeOfAccident" }
  ];

  const ExportCSV = ({ data }) =>
      <CSVLink data={data} headers={headers} filename="mlc-entries.csv">
        <button className="btn btn-primary mt-1" id="export">
          Export
        </button>
        <UncontrolledTooltip placement="top" target="export">
          Export to CSV
        </UncontrolledTooltip>
      </CSVLink>;

  // ================= RENDER =================
  return (
    <Fragment>
      <Breadcrumb parent="MLC" title="MLC Entries" />

      <div className="container-fluid">
        <div className="card">
          <div className="card-header d-flex justify-content-between">
            <h5>MLC Entries</h5>

            <Link to="/ipd/transaction/mlc-entry/add">
              <button className="btn btn-primary" id="create">
                Create MLC Entry
              </button>
              <UncontrolledTooltip placement="top" target="create">
                Create New MLC Entry
              </UncontrolledTooltip>
            </Link>
          </div>

          <div className="card-body">
            <div className="row mb-2">
              <div className="col-md-6">
                <input
                  className="form-control"
                  placeholder="Search..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>

              <div className="col-md-6 text-end">
                <ExportCSV data={data} />
              </div>
            </div>

            <DataTable
              columns={columns}
              data={filteredData}
              pagination
              highlightOnHover
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ListMlcEntry;
